import { useCallback, useEffect, useRef } from 'react';
import { finishRound, startRound } from '../gameRoundApi';
import { useActiveTimer } from './useActiveTimer';

const DAILY_GAME_TYPE = 'daily';

/**
 * Tracks a Daily game-round lifecycle for signed-in users.
 *
 * This hook keeps Daily points in the same server-authoritative path as
 * other modes by routing completion through game-round start/finish.
 */
export const useDailyRoundTracking = ({ userId, onFinish } = {}) => {
  const activeTimer = useActiveTimer();
  const scopeKeyRef = useRef('');
  const roundIdRef = useRef(null);
  const startPromiseRef = useRef(null);
  const finishedScopesRef = useRef(new Set());

  const stopTracking = useCallback(() => {
    activeTimer.stop();
    roundIdRef.current = null;
    startPromiseRef.current = null;
  }, [activeTimer]);

  const beginTracking = useCallback(
    async ({ puzzleDateUtc }) => {
      if (!userId || !puzzleDateUtc) return null;
      const scopeKey = `${userId}:${puzzleDateUtc}`;

      if (scopeKeyRef.current !== scopeKey) {
        scopeKeyRef.current = scopeKey;
        roundIdRef.current = null;
        startPromiseRef.current = null;
        activeTimer.start();
      }

      if (finishedScopesRef.current.has(scopeKey)) return null;
      if (roundIdRef.current) return roundIdRef.current;
      if (startPromiseRef.current) return startPromiseRef.current;

      const pending = startRound({ gameType: DAILY_GAME_TYPE })
        .then((id) => {
          if (scopeKeyRef.current !== scopeKey) return null;
          roundIdRef.current = id || null;
          return roundIdRef.current;
        })
        .catch(() => null)
        .finally(() => {
          if (scopeKeyRef.current === scopeKey) {
            startPromiseRef.current = null;
          }
        });

      startPromiseRef.current = pending;
      return pending;
    },
    [activeTimer, userId],
  );

  const finishTracking = useCallback(
    async ({ puzzleDateUtc, won, guessesUsed }) => {
      if (!userId || !puzzleDateUtc) return null;
      const scopeKey = `${userId}:${puzzleDateUtc}`;
      if (finishedScopesRef.current.has(scopeKey)) return null;

      if (scopeKeyRef.current !== scopeKey) {
        scopeKeyRef.current = scopeKey;
        roundIdRef.current = null;
        startPromiseRef.current = null;
      }

      let roundId = roundIdRef.current;
      if (!roundId) {
        roundId = await beginTracking({ puzzleDateUtc });
      }

      const activeTimeMs = activeTimer.stop();
      if (!roundId) return null;

      finishedScopesRef.current.add(scopeKey);
      roundIdRef.current = null;
      startPromiseRef.current = null;

      let result = null;
      try {
        result = await finishRound({
          roundId,
          won,
          guessesUsed,
          activeTimeMs,
        });
      } catch {
        result = null;
      }

      if (result && typeof onFinish === 'function') {
        onFinish(result);
      }

      return result;
    },
    [activeTimer, beginTracking, onFinish, userId],
  );

  useEffect(() => {
    if (!userId) {
      stopTracking();
      scopeKeyRef.current = '';
      finishedScopesRef.current = new Set();
    }
  }, [stopTracking, userId]);

  useEffect(
    () => () => {
      stopTracking();
    },
    [stopTracking],
  );

  return {
    beginTracking,
    finishTracking,
    stopTracking,
  };
};

